import { TaskDoc } from "@kwp/contracts";
import { isTaskCommand, parseTaskCommand, type TaskCommand } from "@kwp/domain";
import { reviveTimestamps } from "@kwp/firestore";

/** A task as the screens use it: the stored document plus its id. */
export type Task = TaskDoc & { id: string };

export const VIEWS = [
  { key: "mine", label: "Mine" },
  { key: "assignedByMe", label: "Assigned by me" },
  { key: "private", label: "Private" },
  { key: "shared", label: "Shared" },
  { key: "all", label: "All" },
] as const;
export type TaskView = (typeof VIEWS)[number]["key"];
export type StatusTab = "open" | "done" | "cancelled";
export const STATUSES: ReadonlyArray<{ key: StatusTab; label: string }> = [
  { key: "open", label: "Open" },
  { key: "done", label: "Done" },
  { key: "cancelled", label: "Cancelled" },
];

/** Reads one Firestore task document; a document that does not match the contract is skipped. */
export function toTask(id: string, data: unknown): Task | null {
  const parsed = TaskDoc.safeParse(reviveTimestamps(data));
  return parsed.success ? { ...parsed.data, id } : null;
}

/**
 * Rules are not filters (architecture §8): the screen listens to several queries the rules can
 * prove (workspace-visible, created by me, assigned to me) and merges them here, newest first.
 */
export function mergeTasks(...lists: ReadonlyArray<readonly Task[]>): Task[] {
  const byId = new Map<string, Task>();
  for (const list of lists) for (const t of list) byId.set(t.id, t);
  return [...byId.values()].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

/** The same view semantics as the api's `tasks.list` (CLI and MCP). */
export function inView(task: Task, view: TaskView, me: string): boolean {
  switch (view) {
    case "mine":
      return task.assigneeUid === me || (task.assigneeUid === null && task.creatorUid === me);
    case "assignedByMe":
      return task.creatorUid === me && task.assigneeUid !== null && task.assigneeUid !== me;
    case "private":
      return task.visibility === "private";
    case "shared":
      return task.visibility === "workspace";
    case "all":
      return true;
  }
}

export interface TaskFilter {
  view: TaskView;
  status: StatusTab;
  search: string;
}

export function filterTasks(tasks: readonly Task[], filter: TaskFilter, me: string): Task[] {
  const needle = filter.search.trim().toLowerCase();
  return tasks.filter(
    (t) =>
      t.status === filter.status &&
      inView(t, filter.view, me) &&
      (needle.length === 0 || t.title.toLowerCase().includes(needle)),
  );
}

export type DueState = "overdue" | "today" | "soon" | "later" | "none";

const DAY_MS = 24 * 3600 * 1000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** How urgent a due date is, in the viewer's local time: "soon" is within the next two days. */
export function dueState(due: Date | null, now: Date): DueState {
  if (due === null) return "none";
  const today = startOfDay(now).getTime();
  const day = startOfDay(due).getTime();
  if (day < today) return "overdue";
  if (day === today) return "today";
  if (day - today <= 2 * DAY_MS) return "soon";
  return "later";
}

/** My open tasks due today or overdue, most overdue first (the dashboard's Today panel). */
export function dueTodayForMe(tasks: readonly Task[], me: string, now: Date): Task[] {
  return tasks
    .filter((t) => t.status === "open" && inView(t, "mine", me))
    .filter((t) => {
      const s = dueState(t.due, now);
      return s === "overdue" || s === "today";
    })
    .sort((a, b) => (a.due === null || b.due === null ? 0 : a.due.getTime() - b.due.getTime()));
}

export type DueGroup = "overdue" | "today" | "week" | "later" | "none";

export const DUE_GROUP_LABEL: Record<DueGroup, string> = {
  overdue: "Overdue",
  today: "Today",
  week: "Next 7 days",
  later: "Later",
  none: "No date",
};

const GROUP_ORDER: readonly DueGroup[] = ["overdue", "today", "week", "later", "none"];

function dueGroup(due: Date | null, now: Date): DueGroup {
  const state = dueState(due, now);
  if (state === "none" || state === "overdue" || state === "today") return state;
  if (due !== null && startOfDay(due).getTime() - startOfDay(now).getTime() <= 7 * DAY_MS) return "week";
  return "later";
}

/**
 * Open tasks grouped by when they are due, in the viewer's local time: overdue, today, the next
 * seven days, later, then undated. Dated groups run soonest first; undated keeps the list order.
 */
export function groupByDue(tasks: readonly Task[], now: Date): Array<{ group: DueGroup; tasks: Task[] }> {
  const soonest = (a: Task, b: Task) => (a.due === null || b.due === null ? 0 : a.due.getTime() - b.due.getTime());
  return GROUP_ORDER.flatMap((group) => {
    const list = tasks.filter((t) => dueGroup(t.due, now) === group);
    if (group !== "none") list.sort(soonest);
    return list.length === 0 ? [] : [{ group, tasks: list }];
  });
}

/** One letter for a person's avatar: the first letter of their name or email. */
export function initialOf(label: string): string {
  const first = label.trim().charAt(0);
  return first === "" ? "?" : first.toUpperCase();
}

export function formatDue(due: Date, now: Date): string {
  const state = dueState(due, now);
  if (state === "today") return "Today";
  const tomorrow = startOfDay(now).getTime() + DAY_MS;
  if (startOfDay(due).getTime() === tomorrow) return "Tomorrow";
  const sameYear = due.getFullYear() === now.getFullYear();
  return due.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Quick add accepts `/task …` with or without the prefix; the api requires it. */
export function toCommand(text: string): string {
  const trimmed = text.trim();
  return isTaskCommand(trimmed) ? trimmed : `/task ${trimmed}`;
}

export type QuickAddPreview = { ok: true; command: TaskCommand } | { ok: false; error: string } | null;

/**
 * What the quick-add bar will create, parsed with the same parser the api uses, so a person sees
 * the title, assignee, due date and visibility before pressing Enter. Null for an empty bar.
 */
export function previewQuickAdd(text: string, now: Date): QuickAddPreview {
  if (text.trim().length === 0 || /^\s*\/task\s*$/i.test(text)) return null;
  try {
    return { ok: true, command: parseTaskCommand(toCommand(text), now) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Local date input value (yyyy-mm-dd) for a due date, and back (5 pm local on that day). */
export function toDateInput(due: Date | null): string {
  if (due === null) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;
}

export function fromDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m === null) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 17, 0, 0);
}

export const SOURCE_LABEL: Record<NonNullable<TaskDoc["source"]>["kind"], string> = {
  email: "Email",
  chat: "Chat",
  meeting: "Meeting",
  cli: "CLI",
};
